import "./CreateForm.scss"
import {useState, useEffect} from "react";
import {DndContext, MeasuringStrategy} from "@dnd-kit/core";
import EditModal from "../EditModal/EditModal";
import Button from "../../../components/Button/Button";
import {useNavigate, useParams} from "react-router-dom";
import "../../../components/TextStyle.scss"
import ElementsSidebar from "../ElementsSidebar/ElementsSidebar";
import CanvasBuilder from "../CanvasBuilder/CanvasBuilder";
import PageLayout from "../../../components/PageLayout/PageLayout";
import {formsIcon} from "../../../components/Icon/Icon";
import FormActions from "../../../api/FormsActions";
import {useFormik} from "formik";
import {customCollisionDetection, deleteChildren, getRootItems, sortItems} from "../../../utils/Utils";
import {defaultSettings} from "../defaultSettings/defaultSettings";
import {elementAmount} from "../../../components/AreaTypes/AreaTypes";

const CreateForm = () => {
    const navigate = useNavigate();
    const {id} = useParams();
    const [editItem, setEditItem] = useState(null);
    const [error, setError] = useState("");

    const formik = useFormik({
        initialValues: {
            name: null,
            requiresSignature: false,
            content: null
        },
        onSubmit: values => {
            const body = {
                id: values.id,
                name: values.name,
                content: { droppedItems: values.droppedItems }
            }

            FormActions.updateForm(values.id, body, res => {
                navigate("/forms")
            })
        }
    })


    useEffect(() => {
        if(!id) return;

        if (id) {
            FormActions.getForm(id, (res) => {
                const droppedItems = res.content?.droppedItems ?? [];
                const initialDroppedItems = structuredClone(droppedItems);
                const radioGroups = (res.content?.droppedItems ?? [])
                    .filter(droppedItem => droppedItem.settings.inputType === "radio")
                    .map(droppedItem => droppedItem.settings.selectedRadioGroup);

                formik.setValues({
                    ...res,
                    droppedItems,
                    initialDroppedItems,
                    radioGroups,
                })
            });
        }
    }, [id]);

    const handleDragEnd = (event) => {
        const {active, over} = event;
        if (!over) return;

        const type = active.id.split("-")[0];
        const label = active.id.split("-")[1];

        if (!["input", "area", "text"].includes(type)) {
            if (active.id === over.id) return;

            const droppedItems = formik.values.droppedItems;

            const mappedDroppedItems = droppedItems.map(items => {
                const oldIndex = items.findIndex((i) => i.id === active.id);
                const newIndex = items.findIndex((i) => i.id === over.id);

                if (oldIndex !== -1 && newIndex !== -1) {
                    const oldItem = droppedItems[oldIndex];
                    const newItem = droppedItems[newIndex];
                    const oldItemAreaType = oldItem?.settings.areaType?.split("-")[1];
                    const newItemAreaType = newItem?.settings.areaType?.split("-")[1];

                    return sortItems(items, droppedItems, oldIndex, newIndex, oldItemAreaType, newItemAreaType);
                }
                return items;
            })

            formik.setFieldValue('droppedItems', mappedDroppedItems);

            return;
        }

        const newElement = defaultSettings(active.id);

        const isOverMainList = over.id === "main-droppable" || getRootItems(formik.values.droppedItems).some(i => i.id === over.id);

        if ((isOverMainList || over.id.startsWith("droppable-")) && type === "area") {
            addAreas(0, elementAmount[label], active.id, false);
        } else if (over.id.startsWith("droppable-") && type !== "area") {
            newElement.parentId = over.id.replace("droppable-", "");
            formik.setFieldValue('droppedItems', [...formik.values.droppedItems, newElement])
            setEditItem(formik.values.droppedItems.find(item => item.id === newElement.id));
        }
    }

    const addAreas = (start, end, label, sort, oldAreaTypeValue, deleteItemIndex) => {
        if (sort) {
            const addedItems = [];

            if (oldAreaTypeValue === 2) deleteItemIndex -= 1;
            else if (oldAreaTypeValue === 3) deleteItemIndex -= 2;

            for (let i = start; i < end; i++) {
                const element = defaultSettings(label);
                addedItems.push(element);
            }

            formik.setFieldValue('droppedItems', [
                ...formik.values.droppedItems.slice(0, deleteItemIndex),
                ...addedItems,
                ...formik.values.droppedItems.slice(deleteItemIndex)
            ])
        } else {

            for (let i = start; i < end; i++) {
                const element = defaultSettings(label)
                formik.setFieldValue('droppedItems', prev => [...prev, element])
            }
        }
    }


    const saveItemSettings = (id, newSettings) => {
        let droppedItems = formik.values.droppedItems;

        const deleteItem = droppedItems.find(prev => prev.id === id);

        const oldAreaTypeValue = elementAmount[deleteItem.settings.areaType?.split("-")[1]];
        const newAreaTypeValue = elementAmount[newSettings.areaType?.split("-")[1]];

        if (oldAreaTypeValue !== undefined && oldAreaTypeValue !== newAreaTypeValue) {
            const parentIds = deleteChildren(deleteItem, oldAreaTypeValue, droppedItems);

            const hasChildren = droppedItems.find(item => parentIds.includes(item.parentId));

            if (hasChildren) {
                setError("Area has children elements, to change area type you need to delete all children elements");
                return;
            }

            droppedItems = droppedItems.filter(droppedItem =>
                droppedItem.id.substring(0, 18) !== deleteItem.id.substring(0, 18) &&
                !parentIds.includes(droppedItem.parentId) &&
                !parentIds.includes(droppedItem.id)
            );


            addAreas(0, newAreaTypeValue, newSettings.areaType, true, oldAreaTypeValue, droppedItems.indexOf(deleteItem))
        }

        droppedItems = droppedItems.map(item => item.id === id ? {...item, settings: newSettings} : item);

        formik.setFieldValue("droppedItems", droppedItems);

        setEditItem(null);
    }

    const deleteItem = (item) => {
        const amount = elementAmount[item.settings.areaType?.split("-")[1]];

        const droppedItems = formik.values.droppedItems;

        if (amount) {
            const parentIds = deleteChildren(item, amount, droppedItems);

            const filteredItems = droppedItems.filter(droppedItem =>
                droppedItem.id.substring(0, 18) !== item.id.substring(0, 18) &&
                !parentIds.includes(droppedItem.parentId) &&
                !parentIds.includes(droppedItem.id)
            );

            formik.setFieldValue('droppedItems', filteredItems)
        } else {
            formik.setFieldValue('droppedItems', droppedItems.filter(droppedItem => droppedItem.id !== item.id));
        }
        setEditItem(null);
    }


    return (
        <PageLayout
            icon={formsIcon}
            titlePage="Forms"
            titlePageContent={formik.values.name}
            actions={
                <>
                    <Button variant="primary" onClick={formik.handleSubmit}>
                        Save
                    </Button>

                    <Button variant="secondary" onClick={() => {}}>
                        Cancel
                    </Button>
                </>
            }
        >
            <DndContext
                onDragEnd={handleDragEnd}
                collisionDetection={customCollisionDetection}
                layoutMeasuring={{strategy: MeasuringStrategy.Always}}
            >
                <section className="create-form">
                    <ElementsSidebar/>
                    <CanvasBuilder
                        rootItems={getRootItems(formik.values.droppedItems)}
                        droppedItems={formik.values.droppedItems}
                        setEditItem={setEditItem}
                    />
                </section>

                {editItem &&
                    <EditModal
                        item={editItem}
                        radioGroups={formik.values.radioGroups}
                        setRadioGroups={(values) => formik.setFieldValue('radioGroup', values)}
                        onConfirm={saveItemSettings}
                        onDelete={deleteItem}
                        onClose={() => setEditItem(null)}
                        error={error}
                        setError={setError}
                    />
                }
                {/*{unsavedChanges && (*/}
                {/*    <UnsavedChangesModal*/}
                {/*        onClose={() => {*/}
                {/*            setUnsavedChanges(false);*/}
                {/*            navigate(desiredUrl);*/}
                {/*        }}*/}
                {/*        handleSave={() => {*/}
                {/*            setUnsavedChanges(false);*/}
                {/*            handleSave(desiredUrl)*/}
                {/*        }}*/}
                {/*        message={`You have unsaved changes in the "${formName}" form. Are you sure you want to leave?`}*/}
                {/*    />*/}
                {/*)}*/}
            </DndContext>
        </PageLayout>
    )
}

export default CreateForm;